import React, { useState, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Autocomplete,
} from "@mui/material";
import global, { createAppResourceService } from "@/global";
import { useActAs } from "@/contexts/ActAsContext";
import type { ClinicianIdRecord } from "@/pages/settings/components/schemas";

const CLINICIAN_IDS_ID = "trier";
//@ts-ignore
const cliniciansService = createAppResourceService<{ clinicianIds: ClinicianIdRecord[] }>("meta/clinicians");

function getDisplayName(record: ClinicianIdRecord): string {
  if (record.firstName || record.lastName) {
    return [record.firstName, record.lastName].filter(Boolean).join(" ").trim();
  }
  return record.clinicianId ?? record.id ?? "Unknown";
}

function getOptionLabel(record: ClinicianIdRecord): string {
  const name = getDisplayName(record);
  const ref = record.reference?.trim();
  if (ref === undefined || ref === "") return name;
  return `${name} – ${ref}`;
}

export type ActAsModalProps = {
  open: boolean;
  onClose: () => void;
  /** When true, this is the re-auth flow (token expired/invalid) */
  isReauth?: boolean;
};

export const ActAsModal: React.FC<ActAsModalProps> = ({ open, onClose, isReauth = false }) => {
  const auth = useAuth();
  const { actAsState, startActingAs, stopActingAs, dismissReauthModal } = useActAs();
  const currentUserOid = String(auth.user?.profile?.oid ?? "");

  const { data, isLoading } = cliniciansService.useGetOne(CLINICIAN_IDS_ID, {
    refetchOnWindowFocus: false,
    enabled: open,
  });

  const clinicianList = useMemo(() => {
    const ids = Array.isArray(data?.clinicianIds) ? data.clinicianIds : [];
    return ids.filter((r) => r.identityId !== currentUserOid);
  }, [data?.clinicianIds, currentUserOid]);

  const [selected, setSelected] = useState<ClinicianIdRecord | null>(null);
  const [operatorCode, setOperatorCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operatorCodeRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setSelected(null);
    setOperatorCode("");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    if (isReauth) {
      stopActingAs();
    } else {
      dismissReauthModal();
    }
    onClose();
  };

  // Re-auth: pre-select current acting-as clinician if we have it
  const reauthClinician = useMemo(() => {
    if (!isReauth) return null;
    const recordId = actAsState.actAsRecordId;
    if (!recordId) return null;
    return clinicianList.find((c) => c.id === recordId) ?? null;
  }, [isReauth, actAsState.actAsRecordId, clinicianList]);

  const effectiveClinician = isReauth ? reauthClinician : selected;

  const handleSubmit = async () => {
    const clinician = effectiveClinician;
    if (!clinician?.clinicianId || !clinician?.id) {
      setError("Please select a clinician.");
      return;
    }
    if (!operatorCode.trim()) {
      setError("Please enter the operator code.");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const res = await global.client.post("meta/custom/initiate-act-as", {
        actAsClinicianId: clinician.clinicianId,
        actAsRecordId: clinician.id,
        operatorCode: operatorCode.trim(),
      });
      const token = (res as { data?: { token?: string } })?.data?.token;
      if (!token) {
        setError("Invalid response from server.");
        return;
      }
      const displayName = getDisplayName(clinician);
      startActingAs(token, displayName, clinician.clinicianId, clinician.id);
      resetForm();
      dismissReauthModal();
      onClose();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (err as Error)?.message
        ?? "Verification failed. Please check the operator code and try again.";
      setError(message);
      requestAnimationFrame(() => {
        const input = operatorCodeRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const showList = !isReauth && !selected;
  const showPinStep = selected !== null || (isReauth && reauthClinician !== null);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isReauth ? "Re-enter operator code" : "Act as another clinician"}
      </DialogTitle>
      <DialogContent>
        {isReauth && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            There was a problem starting the act as session. Please try re-entering the operator code or cancel.
          </Alert>
        )}

        {showList && (
          <>
            {isLoading ? (
              <Typography variant="body2">Loading clinicians…</Typography>
            ) : clinicianList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No other clinicians configured.
              </Typography>
            ) : (
              <Autocomplete
                value={selected}
                onChange={(_, newValue) => {
                  setSelected(newValue);
                  setError(null);
                }}
                options={clinicianList}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                fullWidth
                autoHighlight
                openOnFocus
                sx={{
                  mt: 1,
                  "& .MuiInputBase-root": {
                    height: "38.99px",
                    padding: "0 9px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "8.5px 4px !important",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    label="Clinician"
                    placeholder="Search by name or reference"
                    variant="outlined"
                    error={!!error}
                    helperText={error}
                  />
                )}
              />
            )}
          </>
        )}

        {showPinStep && (
          <Box sx={{ mt: 2 }}>
            {effectiveClinician && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Acting as: {getDisplayName(effectiveClinician)}
              </Typography>
            )}
            <TextField
              fullWidth
              autoFocus
              inputRef={operatorCodeRef}
              label="Operator code"
              type="password"
              autoComplete="current-password"
              value={operatorCode}
              onChange={(e) => {
                setOperatorCode(e.target.value);
                setError(null);
              }}
              error={!!error}
              helperText={error}
              disabled={submitting}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {showPinStep && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Verifying…" : "Confirm"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
